import { useEffect, useState, useCallback, useRef } from "react";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Alert, AlertDescription } from "../components/ui/alert";
import {
  Save,
  X,
  RefreshCw,
  Clock,
  AlertCircle,
  Info,
  Store,
  ShoppingBag,
  Truck,
  UtensilsCrossed,
  Upload,
  Image as ImageIcon,
  Trash2,
  QrCode,
  Link as LinkIcon,
} from "lucide-react";
import { useToast } from "../components/ui/use-toast";
import {
  server_post_data,
  save_restaurant_setting,
  get_restaurant_setting,
  APL_LINK,
} from "../ServiceConnection/serviceconnection";

export default function OutletSettingCompo() {
  const { toast } = useToast();
  const fileInputRef = useRef(null);

  // State management
  const [showLoaderAdmin, setShowLoaderAdmin] = useState(false);
  const [selectedTime, setSelectedTime] = useState(null);
  const [outletTypes, setOutletTypes] = useState({
    dineIn: true,
    takeaway: true,
    delivery: true,
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoFileold, setLogoFileold] = useState("");
  const [logoPreview, setLogoPreview] = useState(null);
  const [existingLogo, setExistingLogo] = useState(null);
  const [removeLogo, setRemoveLogo] = useState(false);
  const [deliveryUrl, setDeliveryUrl] = useState("");
  const [isFormValid, setIsFormValid] = useState(false);

  // Fetch settings data
  const fetchSettingsData = useCallback(async () => {
    setShowLoaderAdmin(true);
    const fd = new FormData();
    fd.append("flag", 1);
    fd.append("want_table_data", 0);

    try {
      const response = await server_post_data(get_restaurant_setting, fd);

      if (!response.data.error) {
        const timeValue =
          response.data.message.data_restaurant[0].default_auto_sync_time;
        setSelectedTime(timeValue || null);

        // Fetch outlet types from API
        const dineInValue =
          response.data.message.data_restaurant[0].restaurant_dinein;
        const takeawayValue =
          response.data.message.data_restaurant[0].restaurant_takeaway;
        const deliveryValue =
          response.data.message.data_restaurant[0].restaurant_delivery;
        setOutletTypes({
          dineIn: dineInValue === 1 || dineInValue === "1",
          takeaway: takeawayValue === 1 || takeawayValue === "1",
          delivery: deliveryValue === 1 || deliveryValue === "1",
        });

        // Fetch QR Menu URL
        const qrMenuUrl =
          response.data.message.data_restaurant[0].delivery_url || "";
        setDeliveryUrl(qrMenuUrl);

        // Fetch existing logo from API
        const logoUrl =
          response.data.message.data_restaurant[0].restaurant_image;
        const image_url = APL_LINK + response.data.message.image_url;
        if (String(logoUrl).length > 0) {
          setExistingLogo(image_url + logoUrl);
        }
        setLogoFileold(logoUrl);
      } else {
        toast({
          title: "Error",
          description: response.data.message,
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error fetching settings:", error);
      toast({
        title: "Error",
        description: "Failed to load settings data",
        variant: "destructive",
      });
    } finally {
      setShowLoaderAdmin(false);
    }
  }, [toast]);

  // Handle outlet type checkbox change
  const handleOutletTypeChange = useCallback(
    (type, checked) => {
      setOutletTypes((prev) => {
        const newTypes = { ...prev, [type]: checked };

        // Check if at least one is still selected
        const hasAtLeastOne = Object.values(newTypes).some((value) => value);

        // If trying to uncheck the last one, prevent it
        if (!hasAtLeastOne) {
          toast({
            title: "Validation Error",
            description: "At least one outlet type must be selected",
            variant: "destructive",
          });
          return prev; // Return previous state, don't update
        }

        return newTypes;
      });
    },
    [toast],
  );

  // Handle logo file selection
  const handleLogoChange = useCallback(
    (event) => {
      const file = event.target.files[0];

      if (file) {
        // Validate file type
        const validTypes = [
          "image/jpeg",
          "image/jpg",
          "image/png",
          "image/gif",
        ];
        if (!validTypes.includes(file.type)) {
          toast({
            title: "Invalid File Type",
            description: "Please select a valid image file (JPG, PNG, or GIF)",
            variant: "destructive",
          });
          return;
        }

        // Validate file size (max 5MB)
        const maxSize = 5 * 1024 * 1024; // 5MB in bytes
        if (file.size > maxSize) {
          toast({
            title: "File Too Large",
            description: "Logo file size must be less than 5MB",
            variant: "destructive",
          });
          return;
        }

        setLogoFile(file);
        setRemoveLogo(false);

        // Create preview
        const reader = new FileReader();
        reader.onloadend = () => {
          setLogoPreview(reader.result);
        };
        reader.readAsDataURL(file);
      }
    },
    [toast],
  );

  // Handle logo removal
  const handleRemoveLogo = useCallback(() => {
    setLogoFile(null);
    setLogoPreview(null);
    setRemoveLogo(true);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, []);

  // Trigger file input click
  const handleUploadClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  // Handle delivery URL change
  const handleDeliveryUrlChange = useCallback((e) => {
    setDeliveryUrl(e.target.value);
  }, []);

  // Handle save
  const handleSave = useCallback(async () => {
    if (!selectedTime || selectedTime === "null") {
      toast({
        title: "Validation Error",
        description: "Please select auto sync time",
        variant: "destructive",
      });
      return;
    }

    const hasAtLeastOneOutletType = Object.values(outletTypes).some(
      (value) => value,
    );
    if (!hasAtLeastOneOutletType) {
      toast({
        title: "Validation Error",
        description: "At least one outlet type must be selected",
        variant: "destructive",
      });
      return;
    }

    setShowLoaderAdmin(true);

    const fd = new FormData();
    fd.append("default_auto_sync_time", selectedTime);
    fd.append("from_where", 2);
    fd.append("restaurant_dinein", outletTypes.dineIn ? 1 : 0);
    fd.append("restaurant_takeaway", outletTypes.takeaway ? 1 : 0);
    fd.append("restaurant_delivery", outletTypes.delivery ? 1 : 0);
    fd.append("restaurant_logo_old", logoFileold);
    fd.append("delivery_url", deliveryUrl);

    // Add logo file if uploaded
    if (logoFile) {
      fd.append("restaurant_logo", logoFile);
    }

    try {
      const response = await server_post_data(save_restaurant_setting, fd);

      if (response.data.error) {
        toast({
          title: "Error",
          description: response.data.message,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Success",
          description: "Settings updated successfully",
        });
        setLogoFile(null);
        setLogoPreview(null);
        setRemoveLogo(false);
        fetchSettingsData(); // Refresh data
      }
    } catch (error) {
      console.error("Save error:", error);
      toast({
        title: "Error",
        description: "Failed to save settings",
        variant: "destructive",
      });
    } finally {
      setShowLoaderAdmin(false);
    }
  }, [
    selectedTime,
    outletTypes,
    logoFile,
    removeLogo,
    deliveryUrl,
    logoFileold,
    toast,
    fetchSettingsData,
  ]);

  // Handle cancel
  const handleCancel = useCallback(() => {
    setLogoFile(null);
    setLogoPreview(null);
    setRemoveLogo(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    fetchSettingsData(); // Reset to saved values
  }, [fetchSettingsData]);

  // Validate form
  useEffect(() => {
    const hasValidTime = selectedTime && selectedTime !== "null";
    const hasAtLeastOneOutletType = Object.values(outletTypes).some(
      (value) => value,
    );
    setIsFormValid(hasValidTime && hasAtLeastOneOutletType);
  }, [selectedTime, outletTypes]);

  // Initial load
  useEffect(() => {
    fetchSettingsData();
  }, [fetchSettingsData]);

  // Determine which logo to display
  const displayLogo =
    logoPreview || (existingLogo && !removeLogo ? existingLogo : null);

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-foreground">
          Outlet Settings
        </h3>
        <p className="text-sm text-muted-foreground mt-1">
          Configure outlet type, logo, QR menu URL, and automatic
          synchronization settings
        </p>
      </div>

      {/* Restaurant Logo Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <ImageIcon className="w-4 h-4 text-primary" />
            Restaurant Logo
          </CardTitle>
          <CardDescription>
            Upload your restaurant logo (JPG, PNG, or GIF - Max 5MB)
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-3">
            <Label className="text-sm font-medium">Logo Image</Label>

            {/* Logo Preview or Upload Area */}
            <div className="flex items-start gap-4">
              {/* Preview Area */}
              {displayLogo ? (
                <div className="relative">
                  <div className="w-32 h-32 rounded-lg border-2 border-dashed border-primary/20 overflow-hidden bg-accent/50">
                    <img
                      src={displayLogo}
                      alt="Restaurant Logo"
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    className="absolute -top-2 -right-2 h-8 w-8 rounded-full p-0"
                    onClick={handleRemoveLogo}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <div className="w-32 h-32 rounded-lg border-2 border-dashed border-muted-foreground/25 flex items-center justify-center bg-accent/50">
                  <ImageIcon className="w-8 h-8 text-muted-foreground/50" />
                </div>
              )}

              {/* Upload Button and Info */}
              <div className="flex-1 space-y-3">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/gif"
                  onChange={handleLogoChange}
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleUploadClick}
                  className="gap-2"
                >
                  <Upload className="w-4 h-4" />
                  {displayLogo ? "Change Logo" : "Upload Logo"}
                </Button>

                <div className="space-y-1 text-sm text-muted-foreground">
                  <p>• Recommended size: 200x200 pixels</p>
                  <p>• Accepted formats: JPG, PNG, GIF</p>
                  <p>• Maximum file size: 5MB</p>
                </div>
              </div>
            </div>
          </div>

          {/* Info Alert */}
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              Your restaurant logo will be displayed on receipts, reports, and
              customer-facing interfaces. For best results, use a square image
              with a transparent background.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>

      {/* Outlet Type Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Store className="w-4 h-4 text-primary" />
            Outlet Type
          </CardTitle>
          <CardDescription>
            Select the service types available at this outlet
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-3">
            <Label className="text-sm font-medium">
              Service Types <span className="text-destructive">*</span>
            </Label>

            <div className="space-y-3">
              {/* Dine-In */}
              <div className="flex items-center space-x-3 p-3 rounded-lg border hover:bg-accent/50 transition-colors">
                <Checkbox
                  id="dineIn"
                  checked={outletTypes.dineIn}
                  onCheckedChange={(checked) =>
                    handleOutletTypeChange("dineIn", checked)
                  }
                />
                <div className="flex items-center gap-2 flex-1">
                  <UtensilsCrossed className="w-4 h-4 text-primary" />
                  <Label
                    htmlFor="dineIn"
                    className="text-sm font-medium cursor-pointer"
                  >
                    Dine-In
                  </Label>
                </div>
              </div>

              {/* Takeaway */}
              <div className="flex items-center space-x-3 p-3 rounded-lg border hover:bg-accent/50 transition-colors">
                <Checkbox
                  id="takeaway"
                  checked={outletTypes.takeaway}
                  onCheckedChange={(checked) =>
                    handleOutletTypeChange("takeaway", checked)
                  }
                />
                <div className="flex items-center gap-2 flex-1">
                  <ShoppingBag className="w-4 h-4 text-primary" />
                  <Label
                    htmlFor="takeaway"
                    className="text-sm font-medium cursor-pointer"
                  >
                    Takeaway
                  </Label>
                </div>
              </div>

              {/* Delivery */}
              <div className="flex items-center space-x-3 p-3 rounded-lg border hover:bg-accent/50 transition-colors">
                <Checkbox
                  id="delivery"
                  checked={outletTypes.delivery}
                  onCheckedChange={(checked) =>
                    handleOutletTypeChange("delivery", checked)
                  }
                />
                <div className="flex items-center gap-2 flex-1">
                  <Truck className="w-4 h-4 text-primary" />
                  <Label
                    htmlFor="delivery"
                    className="text-sm font-medium cursor-pointer"
                  >
                    Delivery
                  </Label>
                </div>
              </div>
            </div>
          </div>

          {/* Validation Message */}
          {!Object.values(outletTypes).some((value) => value) && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                At least one outlet type must be selected
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* QR Menu URL Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <QrCode className="w-4 h-4 text-primary" />
            QR Menu URL
          </CardTitle>
          <CardDescription>
            Enter the URL for your digital QR menu
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-3">
            <Label htmlFor="deliveryUrl" className="text-sm font-medium">
              Menu URL
            </Label>
            <div className="flex items-center gap-3">
              <div className="relative flex-1">
                <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="deliveryUrl"
                  type="url"
                  placeholder="https://yourmenu.com/qr"
                  value={deliveryUrl}
                  onChange={handleDeliveryUrlChange}
                  className="pl-10"
                />
              </div>
            </div>
          </div>

          {/* Info Alert */}
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              This URL will be used to generate QR codes for your digital menu.
              Customers can scan the QR code to view your menu on their mobile
              devices.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>

      {/* Auto Sync Time Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <RefreshCw className="w-4 h-4 text-primary" />
            Default Auto Sync Time
          </CardTitle>
          <CardDescription>
            Set the interval for automatic order synchronization
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-3">
            <Label className="text-sm font-medium">
              Sync Interval <span className="text-destructive">*</span>
            </Label>
            <div className="flex items-center gap-3">
              <Select
                value={selectedTime || "null"}
                onValueChange={(value) => setSelectedTime(value)}
              >
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Select time" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="null" disabled>
                    Select
                  </SelectItem>
                  <SelectItem value="5">5 minutes</SelectItem>
                  <SelectItem value="10">10 minutes</SelectItem>
                  <SelectItem value="15">15 minutes</SelectItem>
                  <SelectItem value="20">20 minutes</SelectItem>
                </SelectContent>
              </Select>
              <Clock className="w-4 h-4 text-muted-foreground" />
            </div>
          </div>

          {/* Info Alert */}
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              The time taken for the orders to be <strong>synced</strong> with
              the dashboard automatically. Please note internet must be
              connected to <strong>enable</strong> auto-sync.
            </AlertDescription>
          </Alert>

          {/* Validation Message */}
          {!isFormValid && selectedTime === "null" && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <AlertCircle className="w-4 h-4" />
              <span>Please select a sync interval to enable auto-sync</span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Additional Info Card */}
      <Card className="border-primary/20 bg-primary/5">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
              <RefreshCw className="w-5 h-5 text-primary" />
            </div>
            <div className="space-y-1">
              <h4 className="text-sm font-semibold text-foreground">
                How Auto Sync Works
              </h4>
              <p className="text-sm text-muted-foreground">
                When enabled, the system will automatically sync orders from
                your POS to the dashboard at the selected interval. This ensures
                your online orders and kitchen display are always up to date.
              </p>
              <ul className="text-sm text-muted-foreground space-y-1 mt-2 list-disc list-inside">
                <li>Requires stable internet connection</li>
                <li>Syncs all pending orders automatically</li>
                <li>Updates inventory and menu availability in real-time</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <div className="flex items-center justify-end gap-4 pt-4 border-t">
        <Button
          variant="outline"
          onClick={handleCancel}
          disabled={showLoaderAdmin}
          className="gap-2"
        >
          <X className="w-4 h-4" />
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={showLoaderAdmin || !isFormValid}
          className="gap-2 gradient-primary text-primary-foreground hover:opacity-90"
        >
          {showLoaderAdmin ? (
            "Saving..."
          ) : (
            <>
              <Save className="w-4 h-4" />
              Save Settings
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
